import { forwardRef, useImperativeHandle } from 'react';

import Button from 'components/partials/button/button';
import Loader from 'components/partials/loader/loader';
import TableComponent from 'components/partials/table-component/table-component';

import AddSpecialAwardModal from './components/add-special-award-modal';
import CalculationSettingsPanel from './components/calculation-settings-panel';
import { useCalculationsStep } from './hooks/use-calculations-step';

interface CalculationsStepProps {
  clientId?: string | number;
  readOnly?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

const CalculationsStep = forwardRef<unknown, CalculationsStepProps>(
  ({ clientId: clientIdProp, readOnly, onDirtyChange }, ref) => {
    const {
      modelId,
      userId,
      clientId,
      calculation,
      settingsToRequest,
      settings,
      visibility,
      disabledGiftTargets,
      giftTargetOptions,
      grantCapOptions,
      setGiftTarget,
      setIncludeRequiredFees,
      setAdjustmentLoans,
      setAdjustmentWorkStudy,
      setCalculateNewNeed,
      setGrantCap,
      setGrantCapAmount,
      rules,
      rulesLoading,
      columns,
      isInitialLoading,
      hasChanges,
      reset,
      save,
      grantCapAmountError,
      isAwardModalOpen,
      editingRule,
      onOpenAddAward,
      onCloseAwardModal,
      onAwardSuccess,
    } = useCalculationsStep({ clientId: clientIdProp, onDirtyChange });

    useImperativeHandle(ref, () => ({ save, discard: reset, hasChanges }), [
      save,
      reset,
      hasChanges,
    ]);

    if (!modelId) {
      return (
        <div className="py-8 text-center text-gray-600">
          Please save the model first to configure calculations.
        </div>
      );
    }

    return (
      <div
        className="relative"
        data-testid="calculations-step"
        inert={readOnly}
      >
        <Loader loading={isInitialLoading} fullScreen={false} />

        {!isInitialLoading && (
          <>
            <CalculationSettingsPanel
              settings={settings}
              visibility={visibility}
              giftTargetOptions={giftTargetOptions}
              grantCapOptions={grantCapOptions}
              disabledGiftTargets={disabledGiftTargets}
              onGiftTargetChange={setGiftTarget}
              onIncludeRequiredFeesChange={setIncludeRequiredFees}
              onAdjustmentLoansChange={setAdjustmentLoans}
              onAdjustmentWorkStudyChange={setAdjustmentWorkStudy}
              onCalculateNewNeedChange={setCalculateNewNeed}
              onGrantCapChange={setGrantCap}
              onGrantCapAmountChange={setGrantCapAmount}
              grantCapAmountError={grantCapAmountError}
            />

            <div className="mt-8" data-testid="special-awards-section">
              <div className="mb-3 flex items-center justify-between gap-4">
                {!!rules.length && (
                  <h3 className="font-semibold text-gray-700">Special award</h3>
                )}

                <Button
                  type="button"
                  variant="reserve-outlined"
                  size="sm"
                  onClick={onOpenAddAward}
                  className="shadow"
                  data-testid="add-special-award-button"
                >
                  Add special award
                </Button>
              </div>

              {!!rules.length && (
                <div className="relative">
                  <TableComponent
                    columns={columns}
                    data={rules}
                    isLoading={rulesLoading}
                    pagination={false}
                  />
                </div>
              )}
            </div>
          </>
        )}

        {isAwardModalOpen && userId && modelId && (
          <AddSpecialAwardModal
            // Remounts between add/edit so the modal starts from a clean form.
            key={editingRule?.id ?? 'new'}
            userId={userId}
            modelId={modelId}
            clientId={clientId ?? ''}
            calculation={calculation}
            settingsToRequest={settingsToRequest}
            rule={editingRule}
            onClose={onCloseAwardModal}
            onSuccess={onAwardSuccess}
          />
        )}
      </div>
    );
  }
);

CalculationsStep.displayName = 'CalculationsStep';

export default CalculationsStep;
